import { graphHooks } from '@tunasong/graph-lib/react'
import { isCollabEntity } from '@tunasong/models'
import { useYDoc } from '@tunasong/plugin-lib'
import type { Audio, Persisted, Poll, PollOption, PollOptionAudio } from '@tunasong/schemas'
import { isAudio } from '@tunasong/schemas'
import { Button, Dialog, Select, Typography } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useEffect, useState } from 'react'
import type { AudioCandidate } from './audio-candidates.js'
import { audioKey, findAudioCandidates } from './audio-candidates.js'
import css from './find-audio-dialog.module.css'
import { getOptionLabel } from './option-label.js'

/**
 * The audio a linked entity offers. A linked song is searched in three places: its child audio, its
 * `hasRecording` edges, and its document (audio blocks, embeds, media links). An option linked straight to an
 * audio entity offers that entity. Loading until the document has been read, so nothing is judged on half the
 * evidence.
 */
const useAudioCandidates = (entityRef?: string) => {
  const { entity } = graphHooks.useEntity(entityRef)
  const { entityClips, isLoading: isLoadingClips } = graphHooks.useEntityClips(entityRef ?? '')
  const isCollab = isCollabEntity(entity)
  const { element } = useYDoc(isCollab ? entity : undefined)
  const nodes = (element as { children?: unknown[] } | null | undefined)?.children ?? []

  const self: Persisted<Audio>[] = isAudio(entity) ? [entity] : []
  const candidates = findAudioCandidates({ clips: [...self, ...(entityClips as Persisted<Audio>[])], nodes })
  const isLoading = !entity || isLoadingClips || (isCollab && !element)
  return { entity, candidates, isLoading }
}

/** Candidates grouped under their headings, in ranking order. */
const byGroup = (candidates: AudioCandidate[]) =>
  candidates.reduce<[string, AudioCandidate[]][]>((groups, candidate) => {
    const last = groups.at(-1)
    if (last?.[0] === candidate.group) {
      last[1].push(candidate)
      return groups
    }
    return [...groups, [candidate.group, [candidate]]]
  }, [])

interface FindAudioRowProps {
  idx: number
  option: PollOption
  /** What the person picked, `null` for "No audio". Undefined until the row has been searched. */
  choice: PollOptionAudio | null | undefined
  onChoose(idx: number, audio: PollOptionAudio | null): void
}

const FindAudioRow: FC<FindAudioRowProps> = props => {
  const { idx, option, choice, onChoose } = props
  const { entity, candidates, isLoading } = useAudioCandidates(option.entityRef)
  const current = option.audio

  /** Audio chosen earlier that the song no longer offers stays selectable, so applying does not drop it silently. */
  const options: AudioCandidate[] =
    current && !candidates.some(c => c.key === audioKey(current))
      ? [{ key: audioKey(current), audio: current, label: 'Chosen earlier', group: 'Current' }, ...candidates]
      : candidates

  /** Keep what was chosen; otherwise propose the best candidate. */
  const proposed = current ?? candidates[0]?.audio ?? null

  /**
   * Report the proposal once the song has been searched, so Apply writes it without the person touching the row.
   * Runs once in effect: after the report, `choice` is defined and this is a no-op.
   */
  useEffect(() => {
    if (!isLoading && choice === undefined) {
      onChoose(idx, proposed)
    }
  }, [isLoading, choice, idx, proposed, onChoose])

  const shown = choice === undefined ? proposed : choice
  const label = getOptionLabel(option, entity?.name)

  return (
    <div className={css.row}>
      <span className={css.name}>{label}</span>
      <Select
        aria-label={`Audio for ${label}`}
        value={shown ? audioKey(shown) : ''}
        onChange={ev => onChoose(idx, options.find(c => c.key === ev.target.value)?.audio ?? null)}
      >
        <option value="">No audio</option>
        {byGroup(options).map(([group, items]) => (
          <optgroup key={group} label={group}>
            {items.map(candidate => (
              <option key={candidate.key} value={candidate.key}>
                {candidate.label}
              </option>
            ))}
          </optgroup>
        ))}
      </Select>
      {isLoading ? (
        <Typography variant="caption" color="textSecondary" className={css.status}>
          Looking for audio…
        </Typography>
      ) : null}
      {!isLoading && options.length === 0 ? (
        <Typography variant="caption" color="textSecondary" className={css.status}>
          No audio found
        </Typography>
      ) : null}
    </div>
  )
}

export interface FindAudioDialogProps {
  poll: Poll
  /** The options to find audio for. Unlinked and removed options are skipped. */
  optionIndices: number[]
  open: boolean
  /** Option index → chosen audio, `null` to clear. Options still being searched are left out. */
  onApply(assignments: Map<number, PollOptionAudio | null>): void
  onClose(): void
}

/**
 * Review the audio found for each linked option and apply it in one go. Each row proposes the best candidate
 * (or keeps what was chosen before); one dialog for the whole poll rather than a prompt per option.
 */
export const FindAudioDialog: FC<FindAudioDialogProps> = props => {
  const { poll, optionIndices, open, onApply, onClose } = props
  const [choices, setChoices] = useState<Map<number, PollOptionAudio | null>>(() => new Map())

  const rows = optionIndices.filter(idx => {
    const option = poll.pollOptions[idx]
    return Boolean(option && !option.removed && option.entityRef)
  })

  const handleChoose = (idx: number, audio: PollOptionAudio | null) =>
    setChoices(current => new Map(current).set(idx, audio))

  const handleApply = () => {
    onApply(choices)
    onClose()
  }

  return (
    <Dialog title="Find audio" open={open} onClose={onClose} maxWidth="sm" fullWidth={true}>
      <Typography variant="body2" className={css.intro}>
        Choose what to play for each option. Audio is looked up on the linked songs: their recordings, and Spotify,
        YouTube and other links in their documents.
      </Typography>
      {rows.map(idx => (
        <FindAudioRow
          key={idx}
          idx={idx}
          option={poll.pollOptions[idx]}
          choice={choices.get(idx)}
          onChoose={handleChoose}
        />
      ))}
      <div className={css.actions}>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleApply} disabled={rows.length === 0}>
          Apply
        </Button>
      </div>
    </Dialog>
  )
}

export default FindAudioDialog
